import {
  Alert,
  AlertTitle,
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Device } from '../types'
import { DeviceType } from '../enums'
import useDialog from '../hooks/useDialog'
import { usePolicy } from '../contexts/PolicyContext'
import { updateDevice } from '../services/deviceService'

export interface DeviceUpdateTypeButtonProps extends Omit<ButtonProps, 'children' | 'type'> {
  device: Device
  type: DeviceType
  onDeviceChange?: (device: Device) => void
}

/**
 * Update the device type. The backend requests the matching firmware with a DFU action.
 */
export default function DeviceUpdateTypeButton({
  device,
  type,
  onDeviceChange,
  ...buttonProps
}: DeviceUpdateTypeButtonProps): React.ReactElement | null {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The policy state.
   */
  const { canEditDevice } = usePolicy()

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * The submitting state.
   */
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  /**
   * The error state.
   */
  const [isError, setError] = useState<boolean>(false)

  /**
   * Close the dialog and reset the error state.
   */
  function handleClose() {
    closeDialog()
    setError(false)
  }

  /**
   * Update the device type.
   */
  async function handleSubmit() {
    try {
      setSubmitting(true)
      setError(false)

      const updatedDevice = await updateDevice(device.name, { type })

      onDeviceChange?.(updatedDevice)
      closeDialog()
    } catch {
      setError(true)
    } finally {
      setSubmitting(false)
    }
  }

  // Render nothing when the user is not allowed or the device already has the type.
  if (!canEditDevice(device) || device.type === type) {
    return null
  }

  return (
    <>
      <Button variant="outlined" color="warning" size="small" {...buttonProps} onClick={openDialog}>
        {t('updateDeviceType')}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('updateDeviceType')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            <AlertTitle sx={{ m: 0 }}>
              {t('updateDeviceTypeWarning', { type: t(`device:${type}`) })}
            </AlertTitle>
          </Alert>

          {isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <AlertTitle sx={{ m: 0 }}>{t('updateDeviceTypeFailed')}</AlertTitle>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="inherit" onClick={handleClose}>
            {t('actions:cancel')}
          </Button>

          <LoadingButton
            variant="contained"
            color="warning"
            loading={isSubmitting}
            onClick={handleSubmit}
          >
            {isError ? t('tryAgain') : t('actions:beginUpdate')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}
